'use client';

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { useUser } from '@clerk/nextjs';
import { getApiBase } from '@/lib/apiBase';

type Currency = 'USD' | 'CAD';

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { isSignedIn } = useUser();
  const [currency, setCurrencyState] = useState<Currency>('USD');
  const [hasLoadedPreference, setHasLoadedPreference] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load preference from API when user signs in
  useEffect(() => {
    if (!isSignedIn) {
      setHasLoadedPreference(false);
      return;
    }

    const loadPreference = async () => {
      try {
        const response = await fetch(`${getApiBase()}/api/preferences`);
        if (response.ok) {
          const data = await response.json();
          if (data.preferences?.currency) {
            setCurrencyState(data.preferences.currency);
          }
        }
      } catch (error) {
        console.error('Failed to load currency preference:', error);
      } finally {
        setHasLoadedPreference(true);
      }
    };

    loadPreference();
  }, [isSignedIn]);

  // Save preference to API (debounced)
  const savePreference = useCallback(async (newCurrency: Currency) => {
    if (!isSignedIn) return;

    try {
      await fetch(`${getApiBase()}/api/preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currency: newCurrency }),
      });
    } catch (error) {
      console.error('Failed to save currency preference:', error);
    }
  }, [isSignedIn]);

  // Wrapper that updates state and persists to API
  const setCurrency = useCallback((newCurrency: Currency) => {
    setCurrencyState(newCurrency);

    // Only save to API if signed in and initial load is complete
    if (isSignedIn && hasLoadedPreference) {
      // Debounce the save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        savePreference(newCurrency);
      }, 300);
    }
  }, [isSignedIn, hasLoadedPreference, savePreference]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
